// GC105 · CIERRE ÚNICO DEL CICLO DE AUDIO — ligado al sesion_id RESERVADO.
// Un solo camino terminal para: colgado del dispositivo (Terminated), error de conexión, cancelación
// voluntaria y desmontaje de la pantalla.
// Reglas:
//  · IDEMPOTENTE: varias peticiones de cierre simultáneas producen UNA sola terminación en backend.
//  · La referencia a la reserva SÓLO se suelta con evidencia de que ya no hay nada que liberar:
//    terminación confirmada (200) o sesión inexistente (404, idempotente).
//  · Un RECHAZO del servidor (403) no cierra nada: la ruta rechaza la solicitud ANTES de terminar la
//    sesión, así que la reserva puede seguir ACTIVA; la referencia se conserva y el estado queda
//    declarado como NO RESUELTO para que se recupere por la vía autorizada.
//  · Red caída o fallo del servidor (5xx): no se afirma liberación; se conserva para reintentar.
//  · El servidor es la AUTORIDAD: este módulo nunca da por desocupado el dispositivo por su cuenta.
export function crearCierreAudio({ detenerPuente, terminarEnBackend }) {
  let sesionId = null;
  let enCurso = null;

  return {
    sesion: () => sesionId,
    pendiente: () => enCurso !== null,
    // Nunca sustituye una reserva que NO consta liberada (un callback tardío o un nuevo intento no
    // pueden hacer perder la referencia a una sesión posiblemente activa).
    fijarSesion(id) {
      if (sesionId && id !== sesionId) return false;
      sesionId = id || null;
      return true;
    },
    async cerrar() {
      if (enCurso) return enCurso;   // doble cierre: misma operación, sin segundo POST
      enCurso = (async () => {
        if (detenerPuente) { try { await detenerPuente(); } catch (_) {} }
        const sid = sesionId;
        if (!sid) return "sin_sesion";
        const r = await terminarEnBackend(sid);
        if (r === "acreditado" || r === "inexistente") sesionId = null;
        return r;
      })();
      try {
        return await enCurso;
      } finally {
        enCurso = null;
      }
    },
  };
}

// Traduce el intento HTTP de terminación. Sólo 200-299 y 404 dejan de tener algo que liberar.
export function resultadoTerminacion(status) {
  if (status >= 200 && status < 300) return "acreditado";   // terminación confirmada
  if (status === 404) return "inexistente";                 // ya no consta: idempotente
  if (status === 403) return "rechazado";                   // NO se cerró: puede seguir activa
  return "sin_confirmar";                                   // red, 5xx y cualquier otra respuesta
}

// ¿El ciclo quedó resuelto (no hay reserva pendiente de liberar)?
export function cierreResuelto(resultado) {
  return resultado === "acreditado" || resultado === "inexistente" || resultado === "sin_sesion";
}

// GC105-C · ASOCIACIÓN caso + generación + sesión. Una reserva pedida para el caso A en la
// generación N sólo pertenece al ciclo vigente si AMBOS coinciden; en cualquier otro caso la
// respuesta llegó TARDE (el operador ya cambió de caso o reinició el audio).
export function destinoReserva(ciclo, vigente) {
  return (ciclo.gen === vigente.gen && ciclo.casoId === vigente.casoId) ? "adoptar" : "liberar";
}

// Libera UNA reserva concreta —la suya— sin tocar la sesión que el ciclo vigente tenga fijada.
export async function liberarReserva(sesionId, terminarEnBackend) {
  if (!sesionId) return "sin_sesion";
  return terminarEnBackend(sesionId);
}

// Entrega de una reserva que puede llegar TARDE: o la adopta el ciclo que la pidió, o se libera
// exclusivamente a sí misma. NUNCA sustituye ni termina la sesión de otro ciclo.
export async function entregarReserva({ sesionId, ciclo, vigente, cierre, terminarEnBackend }) {
  if (destinoReserva(ciclo, vigente) === "adoptar" && cierre.fijarSesion(sesionId)) return "adoptada";
  await liberarReserva(sesionId, terminarEnBackend);
  return "liberada";
}
